/**
 * Os itens do menu lateral (#880): as áreas, a gestão e as seções dos
 * relatórios. Saíram da casca junto com o trilho; a casca (`app/casca`) e a
 * conferência de rota (`rotaPermitida`) os leem daqui.
 */

import type { NomeDoIcone } from '@/shared/ui';

/**
 * O menu NÃO anuncia o que ainda não existe — com uma exceção, as Automações.
 *
 * Havia um mecanismo para isso: `task` num item punha o selo "em breve" e o
 * código da entrega no `title`. Nenhuma área o usava, e ele saiu inteiro —
 * não por estar em uso, mas por estar pronto para voltar a ser usado.
 *
 * Área que não existe não entra no menu. Quando entrar, ela funciona.
 *
 * ── A IA ──────────────────────────────────────────────────────────────────
 *
 * A área da IA é a página comercial do recurso (E12): o que ele faz e o
 * convite para ativar falando com o comercial no WhatsApp. Ela funciona no
 * sentido desta regra — não há botão que finja ligar a IA, e ativar é uma
 * conversa com uma pessoa. Sem selo e sem "em breve", por decisão do dono do
 * produto (29/09/2026).
 *
 * ── A exceção: as Automações (29/09/2026) ─────────────────────────────────
 *
 * Elas entram antes de existir porque divulgá-las é a entrega: o item conta
 * que o recurso vem e leva a uma tela de verdade, que diz o que ele vai fazer,
 * sem prazo. E o botão da tela não finge: "Quero ser um dos primeiros" grava de
 * verdade (migração 066), e a Cliqfy lê a lista para saber quem procurar.
 *
 * O selo depende de um teste pela chave, no `emBreve` do menu logo abaixo, e
 * não de um campo nesta lista: anunciar a próxima área continua exigindo uma
 * decisão escrita aqui, e não uma propriedade a mais num item. Desde a #897 o
 * teste olha também a funcionalidade `automacoes` da organização
 * (`automacoesLiberadas`): ligada, e para quem lê automações, o selo sai.
 */
export const AREAS = [
  { chave: 'atendimento', nome: 'Atendimento', icone: 'atendimento' },
  { chave: 'inbox', nome: 'Fila de conversas', icone: 'fila' },
  // O item é "Comercial", que agrupa; o quadro é o subitem "Funil" (28/10).
  { chave: 'funil', nome: 'Comercial', icone: 'funil' },
  { chave: 'clientes', nome: 'Clientes', icone: 'clientes' },
  { chave: 'tarefas', nome: 'Tarefas', icone: 'tarefas' },
  { chave: 'ia', nome: 'IA', icone: 'ia' },
  { chave: 'automacoes', nome: 'Automações', icone: 'automacao' },
] as const satisfies readonly { chave: string; nome: string; icone: NomeDoIcone }[];

export const GESTAO = [
  { chave: 'painel', nome: 'Relatórios', icone: 'painel' },
  { chave: 'identidade', nome: 'Revisão de identidade', icone: 'identidade' },
  { chave: 'configuracoes', nome: 'Configurações', icone: 'configuracoes' },
] as const satisfies readonly { chave: string; nome: string; icone: NomeDoIcone }[];

/**
 * As seções da área de relatórios (#557). Só as que existem: um item que leva a
 * "em breve" é uma porta que não abre.
 */
export const RELATORIOS = [
  { chave: 'visao-geral', nome: 'Visão geral' },
  { chave: 'ranking', nome: 'Ranking de vendas' },
  { chave: 'gargalos', nome: 'Gargalos do funil' },
  { chave: 'comparativos', nome: 'Comparativos' },
  { chave: 'atendimento', nome: 'Atendimento' },
] as const;

export type SecaoDosRelatorios = (typeof RELATORIOS)[number]['chave'];

/** As áreas da casca: uma por tela, mais as que só existem para algumas organizações. */
export type Area =
  | 'atendimento'
  | 'inbox'
  | 'funil'
  // As etapas do funil, no submenu do Funil (saíram de Configurações em 28/10).
  | 'etapas-do-funil'
  | 'tarefas'
  | 'ia'
  | 'automacoes'
  | 'clientes'
  | 'cliente'
  | 'painel'
  | 'identidade'
  | 'configuracoes'
  // Só a organização de suporte da Cliqfy tem esta área (#561).
  | 'feedbacks'
  // A gestão de organizações: a lista, uma organização, criar uma e o catálogo
  // de planos. Só o Super Admin (emendas de 21/10 e 26/10 ao ADR-0025, #767).
  | 'organizacoes'
  | 'organizacao'
  | 'nova-organizacao'
  | 'planos'
  // Quem é Super Admin, e dar ou tirar o papel: só o Super Admin (ADR-0025).
  | 'super-admins'
  | 'inicio';
// A volta da Meta abre Conexões, onde o assistente de canal continua (ver
// `rotaDaAbertura`).;
